import classNames from 'classnames'
import ArrowDropDownIcon from 'mdi-react/ArrowDropDownIcon'
import React, { useCallback, useRef, useState } from 'react'
import { makeStyles } from 'tss-react/mui'

import { Button, Menu, MenuItem, Theme, Typography } from '@mui/material'

export type MenuButtonProps<MenuOption extends { label: string; value: any }> = {
  className?: string
  'data-testid'?: string
  options: MenuOption[]
  onSelect: (value: MenuOption['value']) => void
  children?: React.ReactNode
  disabled?: boolean
  classes?: {
    button?: string
  }
}

function useMenuButton<Value extends { label: string; value: any }>(onSelect: MenuButtonProps<Value>['onSelect']) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  const handleOpen = useCallback(() => {
    setOpen(true)
  }, [setOpen])
  const handleClose = useCallback(() => {
    setOpen(false)
  }, [setOpen])
  const handleSelect = useCallback(
    (value: Value) => {
      setOpen(false)
      onSelect(value)
    },
    [setOpen, onSelect]
  )
  return {
    open,
    ref,
    handleOpen,
    handleClose,
    handleSelect,
  }
}

function MenuButtonInternal<Value extends { label: string; value: any }>({
  options,
  onSelect,
  className,
  children,
  classes: classesProp = {},
  disabled,
  ...otherProps
}: MenuButtonProps<Value>) {
  const { classes } = useStyles()
  const { open, ref, handleOpen, handleClose, handleSelect } = useMenuButton(onSelect)

  return (
    <div className={className} ref={ref} {...otherProps}>
      <div className={classes.buttonContainer}>
        <Button
          size="small"
          color="secondary"
          variant="outlined"
          onClick={handleOpen}
          aria-haspopup="true"
          disabled={disabled}
          className={classNames(
            classes.moveToButton,
            classes.withArrowDrop,
            { [classes.noContentButton]: !children },
            classesProp.button
          )}
        >
          {children}
          <ArrowDropDownIcon className={classes.arrowDropInline} />
        </Button>
      </div>
      <Menu
        id="simple-menu"
        anchorEl={ref.current}
        open={open}
        onClose={handleClose}
        transformOrigin={{ vertical: -50, horizontal: 'left' }}
      >
        {options.map((option) => (
          <MenuItem className={classes.menuItem} key={option.label} onClick={() => handleSelect(option.value)}>
            <Typography variant="body1">{option.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </div>
  )
}

export const MenuButton = React.memo(MenuButtonInternal)
MenuButton.displayName = 'MenuButton'

const useStyles = makeStyles()(({ spacing }: Theme) => ({
  buttonContainer: {
    position: 'relative',
    display: 'flex',
    height: '100%',
  },
  moveToButton: {
    minWidth: spacing(3.5),
  },
  noContentButton: {
    paddingLeft: 0,
  },
  menuItem: {
    padding: spacing(1, 2),
  },
  withArrowDrop: {
    paddingRight: spacing(3.5),
  },
  arrowDropInline: {
    position: 'absolute',
    top: '50%',
    right: spacing(1 / 4),
    transform: 'translateY(-50%)',
    display: 'inline-block',
  },
}))
